import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
} from 'grommet';
import {
    API,
    approveResume,
    openInNewTab,
    requestResumeEdits,
    resolveSortField,
    ResumeReviewModal,
    RowsPerPageSelect,
    usePaginatedSort,
    userIsResumeReviewer,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useOutletContext } from 'react-router-dom';

const NO_RESUME_LABEL = 'No Resume Available';

const ResumeActions = ({ contact, onComplete }) => {
    const [approveButtonBusy, setApproveButtonBusy] = useState(false);
    const [editButtonBusy, setEditButtonBusy] = useState(false);

    return (
        <Box direction="row" gap="small">
            <Button
                primary
                busy={approveButtonBusy}
                label="Approve"
                onClick={async () => {
                    setApproveButtonBusy(true);
                    await approveResume(contact.id);
                    setApproveButtonBusy(false);
                    onComplete();
                }}
            />
            <Button
                secondary
                busy={editButtonBusy}
                label="Request Edits"
                onClick={async () => {
                    setEditButtonBusy(true);
                    await requestResumeEdits(contact.id);
                    setEditButtonBusy(false);
                    onComplete();
                }}
            />
        </Box>
    );
};

export const ResumeDashboard = () => {
    const user = useOutletContext();
    const isResumeReviewer = userIsResumeReviewer(user);
    const [contacts, setContacts] = useState([]);
    // Bumped after a row leaves the list, to re-fetch so the summary count
    // and the current page both stay honest.
    const [refreshKey, setRefreshKey] = useState(0);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'createdAt', direction: 'desc' },
    });

    useEffect(() => {
        let isActive = true;

        (async () => {
            const contactsResponse = await loadContacts(
                { page, pageSize, sort },
                user,
            );

            if (!isActive) {
                return;
            }

            setContacts(contactsResponse.data);
            applyPagination(contactsResponse.meta);
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, sort, refreshKey, applyPagination]);

    const refreshContacts = () => setRefreshKey((key) => key + 1);

    const dataColumns = [
        {
            header: 'Scholars',
            property: 'attributes.firstName',
            render: (contact) => {
                return (
                    <Anchor
                        href={`/contacts/${contact.id}`}
                        label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Status',
            property: 'attributes.resumeStatus',
            sortable: true,
        },
        {
            header: 'Resume',
            property: 'attributes.documents',
            render: (contact) => {
                const resume = contact.attributes.documents.data.find(
                    (document) =>
                        document.attributes.document_type?.data?.attributes
                            .title == 'Resume',
                );
                if (!resume) return NO_RESUME_LABEL;

                const resumeContent = resume?.attributes?.content;
                if (resumeContent) {
                    return (
                        <ResumeReviewModal
                            contactId={contact.id}
                            documentId={resume.id}
                            content={resumeContent}
                            onComplete={refreshContacts}
                        />
                    );
                }

                const resumeLink = resume?.attributes?.link;
                if (!resumeLink) return NO_RESUME_LABEL;

                return (
                    <Anchor
                        label={'Open Resume'}
                        onClick={() => openInNewTab(resumeLink)}
                    />
                );
            },
        },
        {
            header: 'Actions',
            property: 'attributes.programStatus',
            render: (contact) => (
                <ResumeActions contact={contact} onComplete={refreshContacts} />
            ),
        },
        /* eslint-disable indent */
        ...(!isResumeReviewer
            ? [
                  {
                      header: 'Reviewer',
                      property:
                          'attributes.resumeReviewer.data.attributes.email',
                      sortable: true,
                  },
              ]
            : []),
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Heading level="2">Resume Review</Heading>
                <Box gap="small">
                    <Box>
                        {contacts.length === 0 ? (
                            <Box pad="medium" align="center">
                                <Text>No resumes to review</Text>
                            </Box>
                        ) : (
                            <DataTable
                                primaryKey="id"
                                sort={{ ...sort, external: true }}
                                onSort={handleSort}
                                data={contacts}
                                columns={dataColumns}
                                border={{
                                    body: { side: 'bottom', color: 'border' },
                                }}
                            />
                        )}
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    // Default sort; not tied to a column header.
    createdAt: 'createdAt',
    'attributes.firstName': 'firstName',
    'attributes.resumeStatus': 'resumeStatus',
    'attributes.resumeReviewer.data.attributes.email': 'resumeReviewer.email',
};

const loadContacts = async ({ page, pageSize, sort } = {}, user) => {
    const sortField = resolveSortField(sort, SORT_FIELD_MAP, 'createdAt');
    const sortDir = sort?.direction ?? 'desc';
    const userId = user.id;
    const isResumeReviewer = userIsResumeReviewer(user);
    const resumeReviewerId = user.resumeReviewer?.id;

    const query = QueryString.stringify(
        {
            filters: {
                documents: {
                    document_type: {
                        title: 'Resume',
                    },
                },
                $or: [
                    {
                        isResumeReady: false,
                    },
                    {
                        isResumeReady: null,
                    },
                ],
                // eslint-disable-next-line no-dupe-keys
                $or: [
                    {
                        resumeStatus: 'shared',
                        ...(isResumeReviewer && {
                            resumeReviewer: resumeReviewerId,
                        }),
                    },
                    {
                        resumeStatus: 'edits_completed',
                        ...(isResumeReviewer && {
                            events: {
                                type: 'resume_edits_requested',
                                source_user: userId,
                            },
                        }),
                    },
                ],
            },
            populate: {
                documents: {
                    populate: ['document_type'],
                },
                resumeReviewer: true,
            },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: [`${sortField}:${sortDir}`],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};
